
import React, { useState } from 'react';
import BellIcon from './icons/BellIcon';
import XIcon from './icons/XIcon';
import CartIcon from './icons/CartIcon';
import MeatIcon from './icons/MeatIcon';
import type { Reminder, Language } from '../types';
import { getTranslation } from '../utils/translations';
import { formatIngredientQuantity } from '../utils/unitConversion';

interface RemindersProps {
    isOpen: boolean;
    onClose: () => void;
    reminders: Reminder[];
    language?: Language;
    onAddToCart?: (meatItems: Reminder['meatItems']) => Promise<void>;
}

const Reminders: React.FC<RemindersProps> = ({ isOpen, onClose, reminders, language, onAddToCart }) => {
    const t = (key: any) => getTranslation(language, key);
    const [addingIds, setAddingIds] = useState<Set<string>>(new Set());

    if (!isOpen) return null;

    const handleAddToCart = async (reminder: Reminder) => {
        if (!onAddToCart) return;
        setAddingIds(prev => new Set(prev).add(reminder.id));
        try {
            await onAddToCart(reminder.meatItems);
        } finally {
            setAddingIds(prev => {
                const next = new Set(prev);
                next.delete(reminder.id);
                return next;
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
                <div className="p-4 border-b flex justify-between items-center bg-sky-50 rounded-t-xl">
                    <div className="flex items-center gap-3 text-sky-800">
                        <BellIcon className="h-6 w-6" />
                        <h2 className="text-xl font-bold">{t('reminders')}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1">
                        <XIcon className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-grow space-y-4">
                    {reminders.length > 0 ? (
                        reminders.map(reminder => (
                            <div key={reminder.id} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-md">
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <div className="flex items-center gap-2">
                                        <span className="font-black text-xs uppercase tracking-widest text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full border border-sky-100">
                                            {reminder.text}
                                        </span>
                                        {reminder.type === 'order' ? (
                                            <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md flex items-center gap-1">
                                                <CartIcon className="w-3 h-3" /> {t('buy')}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md flex items-center gap-1">
                                                <MeatIcon className="w-3 h-3" /> {t('defrost')}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-mono text-slate-400">{reminder.date}</span>
                                </div>
                                
                                <div className="space-y-2 mb-4">
                                    {reminder.meatItems.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-700 capitalize">{item.name}</span>
                                                <span className="text-[10px] text-slate-400 italic">{item.recipeName}</span>
                                            </div>
                                            <span className="font-mono text-sky-700 bg-sky-50 px-2 py-0.5 rounded text-xs">
                                                {formatIngredientQuantity(item.quantity, item.unit, t)}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <button 
                                    onClick={() => handleAddToCart(reminder)}
                                    disabled={addingIds.has(reminder.id)}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-bold text-xs transition-all shadow-md shadow-sky-100 disabled:opacity-50"
                                >
                                    {addingIds.has(reminder.id) ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <CartIcon className="w-4 h-4" />
                                    )}
                                    {t('addToActive')}
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="text-center text-slate-400 py-12">
                            <BellIcon className="w-12 h-12 mx-auto mb-4 opacity-10" />
                            <p className="text-sm font-medium">{t('planMealsHint')}</p>
                        </div>
                    )}
                </div>
                 <div className="p-4 bg-slate-50 border-t rounded-b-xl flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300 transition-colors">{t('close')}</button>
                </div>
            </div>
        </div>
    );
};

export default Reminders;
